import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Box,
    Container,
    CircularProgress,
    Alert,
    Button,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';

function ContractPrintPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data: contract, isLoading, isError, error } = useQuery(
        ['contract', id],
        () => JobHttpService.loadContract(id),
        { enabled: !!id }
    );

    useEffect(() => {
        // Print when component mounts if contract is loaded
        if (contract && !isLoading) {
            // Small delay to ensure content is rendered
            setTimeout(() => {
                window.print();
            }, 500);
        }
    }, [contract, isLoading]);

    const handleBack = () => {
        navigate(`/employer/contracts/${id}`);
    };

    const handlePrint = () => {
        window.print();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError || !contract) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">
                    {error?.message || 'Failed to load contract'}
                </Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mt: 2 }}
                >
                    Back
                </Button>
            </Container>
        );
    }

    const company = contract.company;
    const employee = contract.employee;

    return (
        <Box>
            {/* Print controls - hidden when printing */}
            <Box
                sx={{
                    display: 'none',
                    '@media print': {
                        display: 'none'
                    },
                    '@media screen': {
                        display: 'block',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                        p: 2,
                        boxShadow: 1
                    }
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                        >
                            Print
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Contract Content */}
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        '@media print': {
                            boxShadow: 'none',
                            p: 2
                        }
                    }}
                >
                    <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
                        Employment Contract
                    </Typography>

                    {contract.contract_number && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                            Contract Number: {contract.contract_number}
                        </Typography>
                    )}

                    {/* Employer Section */}
                    <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
                        Employer
                    </Typography>
                    <Table sx={{ mb: 4 }}>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ border: '1px solid #000', width: '50%', fontWeight: 600 }}>
                                    Name:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', width: '50%' }}>
                                    {company?.title || '—'}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', width: '50%', fontWeight: 600 }}>
                                    Organization Number:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', width: '50%' }}>
                                    {company?.organization_number || '—'}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Address:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {company?.address || '—'}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Post code:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {company?.postal_code || '—'}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    City:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {company?.city || '—'}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Email:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {company?.email || '—'}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Phone:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {company?.phone_number || '—'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    {/* Employee Section */}
                    <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
                        Employee
                    </Typography>
                    <Table sx={{ mb: 4 }}>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ border: '1px solid #000', width: '50%', fontWeight: 600 }}>
                                    Name:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', width: '50%' }}>
                                    {employee?.first_name || ''} {employee?.last_name || ''}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', width: '50%', fontWeight: 600 }}>
                                    Personal Number:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', width: '50%' }}>
                                    {employee?.employee_number || '—'}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Address:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {employee?.adress || '—'}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    City:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {employee?.city || '—'}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Email:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {employee?.email || '—'}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Phone:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {employee?.phone_number || '—'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    {/* Employment Section */}
                    <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
                        Employment
                    </Typography>
                    <Table sx={{ mb: 4 }}>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Employment Type:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {contract.employment_type || '—'}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Working Hours per Week:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {contract.employment_hours_per_week || '—'}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Position Title:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {contract.employee_position_title || '—'}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Salary:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {contract.salary ? `$${contract.salary.toLocaleString()}` : '—'}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Salary Payment Date:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {formatDate(contract.salary_payment_date)}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Holidays:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {contract.holiday_entitlement || '—'}
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                    Collective Agreement:
                                </TableCell>
                                <TableCell sx={{ border: '1px solid #000' }}>
                                    {contract.collective_agreement || '—'}
                                </TableCell>
                            </TableRow>
                            {contract.insurance_details && (
                                <TableRow>
                                    <TableCell sx={{ border: '1px solid #000', fontWeight: 600 }}>
                                        Insurance Details:
                                    </TableCell>
                                    <TableCell colSpan={3} sx={{ border: '1px solid #000' }}>
                                        {contract.insurance_details}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    {/* Signatures */}
                    <Box sx={{ mt: 6, display: 'flex', justifyContent: 'space-around' }}>
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                Employee Signature: ______________________
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Date: _______________
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                Employer Signature: ______________________
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Date: _______________
                            </Typography>
                        </Box>
                    </Box>

                    {/* Contract Details */}
                    {contract.contract_details && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" gutterBottom fontWeight={600}>
                                Contract Details
                            </Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                {contract.contract_details}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Container>

            {/* Print styles */}
            <style>{`
                @media print {
                    body {
                        margin: 0;
                        padding: 0;
                    }
                    @page {
                        margin: 1cm;
                    }
                }
            `}</style>
        </Box>
    );
}

export default ContractPrintPage;
